import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React from 'react';
import PropTypes from '../../../node_modules/prop-types/index.js';
import { safeRest, noop } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import Body from './Body.js';
import { EXPANDABLE_PANEL_VARIANT } from './constants.js';
import Header from './Header.js';
import { ExpandablePanelWrapper } from './styledComponents.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "title", "titleAs", "onToggle", "ariaLabel", "children", "variant", "customStyle", "expandIcon", "collapseIcon", "expandedByDefault", "isExpanded"];
var ExpandablePanel = function ExpandablePanel(_ref) {
  var controlledId = _ref.id,
    title = _ref.title,
    titleAs = _ref.titleAs,
    onToggle = _ref.onToggle,
    ariaLabel = _ref.ariaLabel,
    children = _ref.children,
    variant = _ref.variant,
    customStyle = _ref.customStyle,
    expandIcon = _ref.expandIcon,
    collapseIcon = _ref.collapseIcon,
    defaultExpanded = _ref.expandedByDefault,
    controlledExpanded = _ref.isExpanded,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDerivedState = useDerivedState(useId('expandable-panel-id'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    expandablePanelStyle = _safeCustomStyle.expandablePanelStyle;
  var _useDerivedState3 = useDerivedState(defaultExpanded, controlledExpanded),
    _useDerivedState4 = _slicedToArray(_useDerivedState3, 2),
    isExpanded = _useDerivedState4[0],
    setExpanded = _useDerivedState4[1];
  return /*#__PURE__*/React.createElement(ExpandablePanelWrapper, _extends({
    id: id,
    "data-testid": id,
    variant: variant,
    expandablePanelStyle: expandablePanelStyle,
    theme: theme
  }, safeProps), /*#__PURE__*/React.createElement(Header, {
    id: "".concat(id, "-header"),
    "data-testid": "".concat(id, "-header"),
    title: title,
    titleAs: titleAs,
    ariaLabel: ariaLabel,
    ariaControls: "".concat(id, "-body"),
    isExpanded: isExpanded,
    onToggle: onToggle,
    expandItem: function expandItem() {
      return setExpanded(!isExpanded);
    },
    customStyle: {
      headerWrapperStyle: customStyle.headerWrapperStyle,
      titleStyle: customStyle.titleStyle
    },
    variant: variant,
    expandIcon: expandIcon,
    collapseIcon: collapseIcon
  }), /*#__PURE__*/React.createElement(Body, {
    id: "".concat(id, "-body"),
    "data-testid": "".concat(id, "-body"),
    ariaLabelledBy: "".concat(id, "-header"),
    isExpanded: isExpanded,
    customStyle: {
      bodyStyle: customStyle.bodyStyle
    },
    variant: variant
  }, children));
};
process.env.NODE_ENV !== "production" ? ExpandablePanel.propTypes = {
  /** DOM element id */
  id: PropTypes.string,
  /** title label for header */
  title: PropTypes.string,
  /** type of component to use as a title (e.g. `h1`, `h2`, etc) */
  titleAs: PropTypes.string,
  /** onToggle event handler */
  onToggle: PropTypes.func,
  /** aria-label */
  ariaLabel: PropTypes.string,
  /** flag to set the panel to be expanded by default */
  expandedByDefault: PropTypes.bool,
  /** User control panel expanded/collapsed state */
  isExpanded: PropTypes.bool,
  /**
   * Icon displayed in collapsed state.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  expandIcon: PropTypes.node,
  /**
   * Icon displayed in expanded state.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  collapseIcon: PropTypes.node,
  /** content to display within the expanded area */
  children: PropTypes.node.isRequired,
  /** Which style of expandable panel to use */
  variant: PropTypes.oneOf([EXPANDABLE_PANEL_VARIANT.PRIMARY, EXPANDABLE_PANEL_VARIANT.SECONDARY]),
  /** style overrides */
  customStyle: PropTypes.shape({
    expandablePanelStyle: PropTypes.object,
    // the wrapper for expandable panel
    headerWrapperStyle: PropTypes.object,
    // the wrapper for title and icon
    titleStyle: PropTypes.object,
    // the title text element
    bodyStyle: PropTypes.object // the wrapper for body
  })
} : void 0;
ExpandablePanel.defaultProps = {
  id: undefined,
  ariaLabel: undefined,
  title: undefined,
  titleAs: 'span',
  onToggle: noop,
  expandedByDefault: false,
  isExpanded: undefined,
  expandIcon: undefined,
  collapseIcon: undefined,
  variant: EXPANDABLE_PANEL_VARIANT.PRIMARY,
  customStyle: {
    expandablePanelStyle: {},
    headerWrapperStyle: {},
    titleStyle: {},
    bodyStyle: {}
  }
};
process.env.NODE_ENV !== "production" ? ExpandablePanel.displayName = 'ExpandablePanel' : void 0;
var ExpandablePanel$1 = ExpandablePanel;

export { ExpandablePanel$1 as default };
